import { Alpine } from '../core/alpine-core.js';
import { apiFetch } from './api.js';

Alpine.data('backupSyncPage', () => ({
  loading: true,
  pushing: false,
  /** When set, a restore is in progress for this snapshot id (other Restore actions stay disabled). */
  restoringSnapshotId: null,
  savingSchedule: false,
  status: null,
  error: '',
  scheduleForm: {
    autoEnabled: false,
    schedulePreset: 'daily',
  },
  restoreDialogOpen: false,
  /** { _id, whenLabel, triggerKind } */
  restorePending: null,
  restoreError: '',

  accountSettingsUpgradeHref() {
    return '/admin/account-settings#plans';
  },

  async init() {
    await this.reload();
  },

  async reload() {
    this.loading = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/admin/backup-sync');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || 'Failed to load status');
      this.status = data;
      this.hydrateScheduleFromStatus();
    } catch (e) {
      this.error = e.message || 'Failed to load';
      this.status = null;
    } finally {
      this.loading = false;
    }
  },

  hydrateScheduleFromStatus() {
    if (!this.status) return;
    this.scheduleForm.autoEnabled = this.status.scheduleAutoEnabledSetting === true;
    this.scheduleForm.schedulePreset = String(this.status.schedulePreset || 'daily');
  },

  schedulePresetOptions() {
    return [
      { value: 'daily', label: 'Every day' },
      { value: 'weekly_sunday', label: 'Each week (Sunday)' },
      { value: 'month_end', label: 'End of month' },
    ];
  },

  schedulePresetLabel(value) {
    const v = String(value || '').trim();
    if (v === 'weekly_sunday') return 'Each week (Sunday)';
    if (v === 'month_end') return 'End of month';
    return 'Every day';
  },

  statusLastSuccessfulSyncLabel() {
    const iso = String(this.status?.lastOkAt || '').trim();
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    }).format(d);
  },

  statusNextRunLabel() {
    const iso = String(this.status?.nextRunAt || '').trim();
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(d);
  },

  snapshotCreatedLabel(iso) {
    const s = String(iso || '').trim();
    if (!s) return '—';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    }).format(d);
  },

  /** From snapshot metadata.note: reason:manual | reason:interval */
  snapshotTriggerKind(snap) {
    const note = String(snap?.metadata?.note ?? snap?.note ?? '').trim();
    if (note.includes('reason:interval')) return 'automatic';
    if (note.includes('reason:manual')) return 'manual';
    return 'unknown';
  },

  snapshotTriggerPillClass(snap) {
    const k = this.snapshotTriggerKind(snap);
    if (k === 'automatic') return 'border-sky-400/40 bg-sky-500/15 text-sky-100';
    if (k === 'manual') return 'border-amber-400/40 bg-amber-500/15 text-amber-100';
    return 'border-white/15 bg-white/10 text-neutral-300';
  },

  snapshotTriggerPillText(snap) {
    const k = this.snapshotTriggerKind(snap);
    if (k === 'automatic') return 'Automatic';
    if (k === 'manual') return 'Manual';
    return 'Backup';
  },

  formatSnapshotBytes(n) {
    const v = Number(n);
    if (!Number.isFinite(v) || v <= 0) return '';
    if (v < 1024) return `${Math.round(v)} B`;
    if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
    if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
    return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  },

  restoreRowDisabled(snap) {
    if (snap?.status !== 'completed') return true;
    if (this.restoringSnapshotId) return true;
    const id = String(snap?._id || '');
    if (this.restoreDialogOpen && String(this.restorePending?._id || '') !== id) return true;
    return false;
  },

  openRestoreDialog(snap) {
    if (this.restoreRowDisabled(snap)) return;
    this.restoreError = '';
    this.restorePending = {
      _id: String(snap._id || ''),
      whenLabel: this.snapshotCreatedLabel(snap.createdAt),
      triggerKind: this.snapshotTriggerPillText(snap),
    };
    this.restoreDialogOpen = true;
  },

  closeRestoreDialog() {
    if (this.restoringSnapshotId) return;
    this.restoreDialogOpen = false;
    this.restorePending = null;
    this.restoreError = '';
  },

  async pushNow() {
    if (this.pushing) return;
    this.pushing = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/admin/backup-sync/push', {
        method: 'POST',
        timeoutMs: 600_000,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        let hint = '';
        if (data?.upgradeRequired) {
          hint = 'Upgrade required for Backup & Restore.';
        } else if (data?.entitlementCheckFailed) {
          hint = 'Could not verify subscription with the control plane.';
        }
        throw new Error([data?.error || data?.message || 'Backup failed', hint].filter(Boolean).join(' '));
      }
      this.$store.toastr.success(
        `Backup complete: ${data.collections ?? '?'} collections, ${data.documents ?? '?'} documents in ${data.ms ?? '?'} ms`,
      );
      if (data?.status) {
        this.status = data.status;
        this.hydrateScheduleFromStatus();
      } else {
        await this.reload();
      }
    } catch (e) {
      this.$store.toastr.error(e.message || 'Backup failed');
      await this.reload();
    } finally {
      this.pushing = false;
    }
  },

  async saveSchedule() {
    if (this.savingSchedule) return;
    this.savingSchedule = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/admin/backup-sync/schedule', {
        method: 'PUT',
        body: {
          autoEnabled: this.scheduleForm.autoEnabled === true,
          schedulePreset: String(this.scheduleForm.schedulePreset || 'daily'),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) throw new Error(data?.error || data?.message || 'Failed to save schedule');
      if (data?.status) this.status = data.status;
      this.hydrateScheduleFromStatus();
      this.$store.toastr.success('Automatic backup schedule updated');
    } catch (e) {
      this.$store.toastr.error(e.message || 'Failed to save schedule');
    } finally {
      this.savingSchedule = false;
    }
  },

  async confirmRestoreFromDialog() {
    const id = String(this.restorePending?._id || '').trim();
    if (!id || this.restoringSnapshotId) return;
    this.restoreError = '';
    this.restoringSnapshotId = id;
    try {
      const res = await apiFetch('/api/admin/backup-sync/restore', {
        method: 'POST',
        timeoutMs: 600_000,
        body: {
          snapshotId: id,
          reason: 'Admin confirmed restore from Backup & Restore page',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        let hint = '';
        if (data?.upgradeRequired) hint = 'Upgrade required for Backup & Restore.';
        else if (data?.entitlementCheckFailed) hint = 'Could not verify subscription with the control plane.';
        throw new Error([data?.error || data?.message || 'Restore failed', hint].filter(Boolean).join(' '));
      }
      this.restoreDialogOpen = false;
      this.restorePending = null;
      this.$store.toastr.success('Restore finished. This API database now matches the selected backup.');
      await this.reload();
    } catch (e) {
      this.restoreError = e.message || 'Restore failed';
      this.$store.toastr.error(this.restoreError);
    } finally {
      this.restoringSnapshotId = null;
    }
  },
}));
